import { FC, useEffect, useState } from 'react';
import { DatePicker, Form, Input, Modal, Radio, Upload } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { Dayjs } from 'dayjs';
import type { RcFile, UploadFile } from 'antd/es/upload/interface';
import { attachBankReceipt, markInvoicePaid } from 'services/api/invoicing';
import { Invoice, InvoicePaymentMethod } from 'types/api/invoicing';

import { showErrorMessage } from 'utils/showErrorMessage';

interface IProps {
  invoice: Invoice | null; // modal is open while non-null
  onClose: () => void;
  onSuccess: () => void;
}

type FormValues = {
  paidAt?: Dayjs | null;
  paymentMethod: InvoicePaymentMethod;
  paymentReference?: string;
  receiptUrl?: string;
  receipt?: { fileList?: UploadFile[] };
};

/** What the API accepts, so the picker cannot offer a file it will refuse. */
const RECEIPT_TYPES = '.pdf,.jpg,.jpeg,.png,.heic,.webp';
const RECEIPT_MAX_BYTES = 10 * 1024 * 1024;

/**
 * Settle an invoice by hand.
 *
 * THE METHOD IS THE POINT OF THIS FORM, not a detail on it. The endpoint
 * never wrote payment_method, so every invoice settled here claimed a
 * Stripe payment — the panel then showed a Stripe receipt for money that
 * had arrived by bank transfer, which is what Grigor reported.
 *
 * The two methods want different evidence, and they are different kinds of
 * thing: Stripe hosts its own receipt and we only hold the link, while a
 * transfer is proven by a document the patient sent us, which we store.
 * So the form asks for one or the other, never both.
 *
 * A bank transfer goes through POST /invoicing/:ref/receipt, which stores
 * the file privately and settles the invoice in ONE call. Uploading first
 * and quoting an id afterwards would leave a stored document that no
 * invoice points at whenever the second call failed.
 */
const MarkPaidModal: FC<IProps> = ({ invoice, onClose, onSuccess }) => {
  const [form] = Form.useForm<FormValues>();
  const [loading, setLoading] = useState(false);
  const method = Form.useWatch('paymentMethod', form);
  const isBankTransfer = method !== InvoicePaymentMethod.Stripe;

  useEffect(() => {
    if (invoice) {
      form.resetFields();
    }
  }, [invoice, form]);

  const onFinish = async (values: FormValues) => {
    if (!invoice) {
      return;
    }

    const paidAt = values.paidAt ? values.paidAt.toISOString() : undefined;
    const file = values.receipt?.fileList?.[0]?.originFileObj;

    try {
      setLoading(true);

      if (values.paymentMethod === InvoicePaymentMethod.Stripe) {
        await markInvoicePaid(invoice.publicId, {
          paidAt,
          paymentMethod: InvoicePaymentMethod.Stripe,
          receiptUrl: values.receiptUrl?.trim() || undefined
        });
      } else {
        // The file is required by the rule below, so this cannot be
        // reached without one; the guard is for the type, not the flow.
        if (!file) return;

        await attachBankReceipt(invoice.publicId, {
          file,
          paidAt,
          paymentReference: values.paymentReference?.trim() || undefined
        });
      }

      onSuccess();
      onClose();
    } catch (err) {
      showErrorMessage(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={!!invoice}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText="Mark paid"
      title={`Mark invoice ${invoice?.invoiceNumber ?? ''} as paid`}
      confirmLoading={loading}
      closable={!loading}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{ paymentMethod: InvoicePaymentMethod.BankTransfer }}
      >
        <Form.Item label="How was it paid" name="paymentMethod">
          <Radio.Group
            options={[
              {
                label: 'Bank transfer',
                value: InvoicePaymentMethod.BankTransfer
              },
              { label: 'Stripe', value: InvoicePaymentMethod.Stripe }
            ]}
            optionType="button"
          />
        </Form.Item>
        <Form.Item
          label="Paid at"
          name="paidAt"
          help="Defaults to now if left empty"
        >
          <DatePicker showTime style={{ width: '100%' }} />
        </Form.Item>
        {isBankTransfer ? (
          <>
            <Form.Item
              label="Receipt"
              name="receipt"
              valuePropName="file"
              // The proof is what makes a hand-settled invoice auditable,
              // so the API requires it and so does the form.
              rules={[
                {
                  validator: (_, value) =>
                    value?.fileList?.length
                      ? Promise.resolve()
                      : Promise.reject(
                          new Error('Attach the receipt the patient sent')
                        )
                }
              ]}
            >
              <Upload
                maxCount={1}
                accept={RECEIPT_TYPES}
                // Held until Save: antd uploads on selection otherwise,
                // and the file only means something alongside the
                // settlement it is proving.
                beforeUpload={(file: RcFile) => {
                  if (file.size > RECEIPT_MAX_BYTES) {
                    showErrorMessage(
                      new Error('The receipt must be under 10 MB')
                    );

                    return Upload.LIST_IGNORE;
                  }

                  return false;
                }}
              >
                <span>
                  <UploadOutlined /> Choose a file
                </span>
              </Upload>
            </Form.Item>
            <Form.Item label="Payment reference" name="paymentReference">
              <Input placeholder="The payer's bank reference (optional)" />
            </Form.Item>
          </>
        ) : (
          <Form.Item label="Receipt URL" name="receiptUrl">
            <Input placeholder="Stripe's hosted receipt (optional)" />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

export default MarkPaidModal;
